import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';
import type {
  FilePackageDownloadResponse,
  FilePackageFilterOptionsResponse,
  FilePackageListQueryParams,
  FilePackageListResponse,
  FilePackageStatusItem,
  ServiceFilePackageCreateRequest,
  ServiceFilePackageCreateResponse,
} from '@/types/filePackage.types';
import type { GenericErrorResponse } from '@/types/api.types';

type ApiResult<T> = { data?: T; error?: GenericErrorResponse };

export const generateServiceFilePackage = async (
  serviceId: string,
  body: ServiceFilePackageCreateRequest
): Promise<ApiResult<ServiceFilePackageCreateResponse>> => {
  try {
    const response = await axiosInstance.post<ServiceFilePackageCreateResponse>(
      `/services/${serviceId}/file-package`,
      body
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useGenerateServiceFilePackage = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ServiceFilePackageCreateResponse,
    GenericErrorResponse,
    { serviceId: string } & ServiceFilePackageCreateRequest
  >({
    mutationFn: async ({ serviceId, workOrders }) => {
      const { data, error } = await generateServiceFilePackage(serviceId, { workOrders });
      if (error) throw error;
      return data!;
    },
    // A cached Downloads list wouldn't have the new In Progress row yet.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [FILE_PACKAGES_QUERY_KEY] }),
  });
};

export const FILE_PACKAGES_QUERY_KEY = 'filePackages';
export const filePackageExportedMessage = (packageName: string) => `${packageName} is successfully exported.`;
// While a package is being generated its row refreshes on this interval.
export const FILE_PACKAGE_REFRESH_MS = 5000;

const unwrap = async <T>(request: Promise<ApiResult<T>>): Promise<T> => {
  const { data, error } = await request;
  if (error) throw error;
  return data!;
};

// Arrays go as JSON strings, as recapService does; the API also accepts repeats.
const toArrayParam = (values?: string[]) => (values?.length ? JSON.stringify(values) : undefined);

export const getFilePackageList = async (
  params: FilePackageListQueryParams
): Promise<ApiResult<FilePackageListResponse>> => {
  try {
    const response = await axiosInstance.get<FilePackageListResponse>('/file-packages', {
      params: {
        ...params,
        clientId: toArrayParam(params.clientId),
        fileNumber: toArrayParam(params.fileNumber),
        caseNumber: toArrayParam(params.caseNumber),
      },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

// Shaped for DataTable's `apiGet`.
export const getFilePackages = (
  params: FilePackageListQueryParams,
  options?: { query?: Partial<UseQueryOptions<FilePackageListResponse>> }
) => {
  const query = useQuery<FilePackageListResponse>({
    queryFn: () => unwrap(getFilePackageList(params)),
    refetchInterval: (current) =>
      current.state.data?.packages?.some((p) => p.status === 'IN_PROGRESS') ? FILE_PACKAGE_REFRESH_MS : false,
    ...options?.query,
    // After the spread: DataTable's own key holds only paging/search values, so
    // on its own it matches other pages' list caches (e.g. Recaps) and this page
    // would render their data. As fbsService does.
    queryKey: [FILE_PACKAGES_QUERY_KEY, params, ...(options?.query?.queryKey ?? [])],
  });
  return { ...query, data: query.data?.packages ?? [], totalRows: query.data?.totalRows ?? 0 };
};

export const getFilePackageFilterOptions = async (): Promise<ApiResult<FilePackageFilterOptionsResponse>> => {
  try {
    const response = await axiosInstance.get<FilePackageFilterOptionsResponse>('/file-packages/filter-options');
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useFilePackageFilterOptions = () =>
  useQuery<FilePackageFilterOptionsResponse, GenericErrorResponse>({
    queryKey: [FILE_PACKAGES_QUERY_KEY, 'filter-options'],
    queryFn: () => unwrap(getFilePackageFilterOptions()),
  });

export const getFilePackageDownload = async (
  filePackageId: string
): Promise<ApiResult<FilePackageDownloadResponse>> => {
  try {
    const response = await axiosInstance.get<FilePackageDownloadResponse>(`/file-packages/${filePackageId}/download`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const getFilePackageStatuses = async (ids: string[]): Promise<ApiResult<FilePackageStatusItem[]>> => {
  try {
    const response = await axiosInstance.get<FilePackageStatusItem[]>('/file-packages/status', {
      params: { ids: JSON.stringify(ids) },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};
